import { useCallback, useEffect, useRef, useState } from 'react';

import type { FilterItemValues } from '../../filters/filter/types/shared-filter-types.js';

/** Typeguard to check if a given record has a subRows property that is an array. */
function hasSubRows<T extends Record<string, unknown>>(
  entry: T,
): entry is T & { subRows: T[] } {
  return (
    // eslint-disable-next-line no-prototype-builtins
    entry && entry.hasOwnProperty('subRows') && Array.isArray(entry['subRows'])
  );
}

/**
 * Facilitates easy usage of the FilterBar for filtering entries in the DataTable.
 *
 * Supports hierarchical data with sub-rows. When filtering, if a parent
 * row doesn't match but one of its sub-rows does, the parent is included
 * with with appropriate sub-rows.
 *
 * @param data - The initial, unfiltered data array.
 * @param filterData - Optional custom filter function that receives the applied filters,
 *   the current entry, its index, and the original data array. Returns `true` to include
 *   the entry in the filtered results. When provided, this overrides the default filter logic.
 * @param subrowMatchingBehavior - Controls how non-matching sibling sub-rows are handled when a
 *   parent or sibling are in the matching path:
 *   - `'includeNonMatchingSiblings'` (default): Include all sibling sub-rows even if they don't match.
 *   - `'excludeNonMatchingSiblings'`: Only include sub-rows that match the filter or are on the path of a match.
 * @returns An object containing:
 *   - `onChange`: Callback to be passed to FilterBar's onChange prop to apply filters.
 *   - `filteredData`: The filtered data array to be used as input for the DataTable.
 *   - `expandedRowIds`: Only relevant for data with sub-rows. IDs of all ancestor rows that contain matching
 *     descendant nodes. This can be used to expand the correct nodes so that the matches
 *     are visible.
 * @public
 */
export function useFilteredData<T extends Record<string, unknown>>(
  data: T[],
  filterData?: (
    appliedFilters: FilterItemValues,
    entry: T,
    index: number,
    originalEntries: T[],
  ) => boolean,
  subrowMatchingBehavior:
    | 'includeNonMatchingSiblings'
    | 'excludeNonMatchingSiblings' = 'includeNonMatchingSiblings',
): {
  onChange: (appliedFilters: FilterItemValues) => void;
  filteredData: T[];
  expandedRowIds?: string[];
} {
  const [filteredData, setFilteredData] = useState(data);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const activeFilters = useRef<FilterItemValues>({});
  const ancestorIdsOfMatchingNodes = useRef<Set<string>>(new Set());

  const defaultFilter = useCallback(
    (appliedFilters: FilterItemValues, entry: T): boolean => {
      const filterKeys = Object.keys(appliedFilters);

      // Early exit if no filters applied
      if (filterKeys.length === 0) {
        return true;
      }

      return filterKeys.every((filterName): boolean => {
        const filterValue = appliedFilters[filterName].value;

        // Skip filters with no value or non-string values
        if (typeof filterValue !== 'string') {
          return true;
        }

        const entryValue = entry[filterName] as T;
        if (!entryValue) {
          return false;
        }
        const lowerFilterValue: string = filterValue.toLowerCase();
        const lowerEntryValue: string = entryValue.toString().toLowerCase();

        return lowerEntryValue.includes(lowerFilterValue);
      });
    },
    [],
  );

  const recursiveFilter = useCallback(
    (
      matchFn: (entry: T) => boolean,
      recursiveFn: (entry: T, ancestors: T[]) => T | null,
    ) => {
      return (entry: T, ancestors: T[] = []): T | null => {
        const entryMatches = matchFn(entry);
        const subRows = hasSubRows(entry) ? entry.subRows : null;

        // Check if the entry itself matches FIRST
        if (entryMatches) {
          // Add all ancestor IDs to the set
          for (let i = 0; i < ancestors.length; i++) {
            const sub = ancestors.slice(0, i + 1);
            const id = sub.map((ancestor) => ancestor.id).join('↳');
            ancestorIdsOfMatchingNodes.current.add(id);
          }

          // If including non-matching siblings and has sub-rows, process them all
          if (
            subrowMatchingBehavior === 'includeNonMatchingSiblings' &&
            subRows?.length
          ) {
            const processedSubRows = subRows.map((subRow: T) => {
              const processed = recursiveFn(subRow, [...ancestors, entry]);
              return processed ?? subRow;
            });

            return {
              ...entry,
              subRows: processedSubRows,
            };
          }

          return entry;
        }

        // Entry doesn't match, so check sub-rows
        if (subRows?.length) {
          // Recursively process all sub-rows
          const processedSubRows = subRows.map((subRow: T) => {
            return recursiveFn(subRow, [...ancestors, entry]);
          });

          // Check if any sub-rows have matches
          const hasMatchingSubRows = processedSubRows.some(
            (subRow: T | null) => subRow !== null,
          );

          if (hasMatchingSubRows) {
            // Include all siblings or only matching ones
            if (subrowMatchingBehavior === 'includeNonMatchingSiblings') {
              const allSubRows = subRows.map(
                (originalSubRow: T, index: number) => {
                  return processedSubRows[index] ?? originalSubRow;
                },
              );

              return {
                ...entry,
                subRows: allSubRows,
              };
            }

            // Only include matching sub-rows
            const filteredSubRows = processedSubRows.filter(
              (subRow: T | null): subRow is T => subRow !== null,
            );

            return {
              ...entry,
              subRows: filteredSubRows,
            };
          }
        }

        // Neither the entry nor any sub-rows match
        return null;
      };
    },
    [subrowMatchingBehavior],
  );

  const defaultRecursiveFilter = useCallback(
    (appliedFilters: FilterItemValues, entry: T, ancestors: T[]): T | null => {
      const filterFn = recursiveFilter(
        (entry) => defaultFilter(appliedFilters, entry),
        (entry, ancestors) =>
          // eslint-disable-next-line react-hooks/immutability
          defaultRecursiveFilter(appliedFilters, entry, ancestors),
      );
      return filterFn(entry, ancestors);
    },
    [defaultFilter, recursiveFilter],
  );

  const customRecursiveFilter = useCallback(
    (appliedFilters: FilterItemValues, entry: T, index: number): T | null => {
      if (!filterData) {
        return null;
      }

      const filterFn = recursiveFilter(
        (entry) => filterData(appliedFilters, entry, index, data),
        // eslint-disable-next-line react-hooks/immutability
        (entry) => customRecursiveFilter(appliedFilters, entry, index),
      );
      return filterFn(entry);
    },
    [data, filterData, recursiveFilter],
  );

  const isCustomFilter = filterData !== undefined && filterData !== null;

  const onChange = useCallback(
    (appliedFilters: FilterItemValues) => {
      activeFilters.current = appliedFilters;
      ancestorIdsOfMatchingNodes.current.clear();

      const filtered = data.reduce<T[]>((acc, entry, index) => {
        const result = isCustomFilter
          ? customRecursiveFilter(appliedFilters, entry, index)
          : defaultRecursiveFilter(appliedFilters, entry, []);
        if (result) {
          acc.push(result);
        }
        return acc;
      }, []);

      setFilteredData(filtered);
      setExpandedRowIds(Array.from(ancestorIdsOfMatchingNodes.current));
    },
    [data, isCustomFilter, defaultRecursiveFilter, customRecursiveFilter],
  );

  // If the incoming data changes, the filter needs to be applied
  // again to the new dataset
  useEffect(() => {
    onChange(activeFilters.current);

    // Only needs to run when the data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    onChange,
    filteredData,
    expandedRowIds,
  };
}
